import { useState } from 'react';
import { css } from '@emotion/react';
import { Dropdown } from '@hotjar/hotsauce';

import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useTrafficExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';

import { TrafficCoverageMessage } from './TrafficCoverageMessage';
import { TrafficStatus } from './TrafficStatus';

interface Props {
  trackIndicatorClick: (elementName: string) => void;
  trackClick: (elementName: string, shownTrafficCoverage?: number | null) => void;
  trackView: (elementName: string, shownTrafficCoverage?: number | null) => void;
  disableAnimation?: boolean;
}

const DIRECT_TRAFFIC_UPSELL_EXPERIMENT_ID = 'bxp_direct_traffic_upsell';

export const SessionTrafficCoverage = ({
  trackIndicatorClick,
  trackClick,
  trackView,
  disableAnimation = false,
}: Props): JSX.Element | null => {
  const [isDropDownShown, setOnShown] = useState<boolean>(false);
  const { data: currentSite, isLoading: isLoadingSite } = useCurrentSite();
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const userRole = currentUser?.role ?? 'other';

  const isOrganizationBiggerThan50 =
    currentSite?.organization?.size !== '1-10' && currentSite?.organization?.size !== '11-50';
  const userCanContactSales =
    (userRole === 'product manager' || userRole === 'ux designer') && isOrganizationBiggerThan50;
  const {
    isLoading: isLoadingTrafficCoverage,
    dailyTraffic,
    days,
    isCoverageMeasureComplete,
    coveragePercentage,
    ccDailyAllowance,
    coverageLevel,
    hasPartialMeasure,
  } = useTrafficCoverage();

  const isLoading = isLoadingSite || isLoadingCurrentUser || isLoadingTrafficCoverage;

  const { inVariant1 } = useTrafficExperiment(
    DIRECT_TRAFFIC_UPSELL_EXPERIMENT_ID,
    {
      id: currentSite?.id,
      coverageLevel,
      days,
    },
    !!currentSite?.id && !isLoading,
  );

  if (!currentSite || isLoading) {
    return null;
  }

  if (inVariant1) {
    return (
      <TrafficStatus
        coveragePercentage={coveragePercentage}
        hasPartialMeasure={hasPartialMeasure}
        isCoverageMeasureComplete={isCoverageMeasureComplete}
        coverageLevel={coverageLevel}
        onClick={() => {
          trackIndicatorClick('traffic_coverage_traffic_coverage_indicator');
          upsell({
            site: currentSite,
            referrer: 'sampling-widget.indicator-direct-upsell',
          }).then(noop, noop);
        }}
        hideDropdownCaret
      />
    );
  }

  return (
    <Dropdown
      onShown={() => setOnShown(true)}
      onHide={() => setOnShown(false)}
      maxWidth={400}
      onShow={() => {
        let elementName;
        if (isCoverageMeasureComplete && coveragePercentage === 100) {
          elementName = 'full_traffic_coverage';
        } else if (isCoverageMeasureComplete) {
          elementName = 'active-traffic-coverage-rate';
        } else {
          elementName = 'measuring_in_progress';
        }
        trackView(elementName, coveragePercentage);
      }}
      trigger={
        <TrafficStatus
          coveragePercentage={coveragePercentage}
          hasPartialMeasure={hasPartialMeasure}
          isCoverageMeasureComplete={isCoverageMeasureComplete}
          coverageLevel={coverageLevel}
          onClick={() => trackIndicatorClick('traffic_coverage_traffic_coverage_indicator')}
        />
      }
      css={css`
        .tippy-content {
          padding: 0;
          overflow-x: hidden;
        }
      `}
    >
      <TrafficCoverageMessage
        site={currentSite}
        isCoverageMeasureComplete={isCoverageMeasureComplete}
        hasPartialMeasure={hasPartialMeasure}
        dailyTraffic={dailyTraffic}
        ccDailyAllowance={ccDailyAllowance}
        coveragePercentage={coveragePercentage}
        trackClick={trackClick}
        userCanContactSales={userCanContactSales}
        disableAnimation={disableAnimation}
        isDropDownShown={isDropDownShown}
      />
    </Dropdown>
  );
};
